import { Counter } from 'components/counter';
import { PriceComposition } from 'components/price-composition';
import { getIntervalTranslationKeyShort, SubscriptionInterval } from 'constants/order';
import { isGiftProductItem, PdProductItem } from 'interfaces/line-item';
import { MinimalCheckoutItem } from 'modules/checkout/order';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatTitle } from 'utils/product';

interface ItemProps {
  item: PdProductItem | MinimalCheckoutItem;
  isCartGift?: boolean;
  canRemoveProducts?: boolean;
  onRemoveItem?: () => void;
}

const Item: React.FC<ItemProps> = ({
  item,
  isCartGift = false,
  canRemoveProducts = false,
  onRemoveItem,
}) => {
  const hasNoVariantTitle = !item.variantTitle || item.variantTitle === '';

  return (
    <div className="flex leading-tight">
      <div className="relative w-16">
        {item.imageSrc && (
          <img className="w-full" src={item.imageSrc} alt={formatTitle(item.productTitle)} />
        )}
        <Counter value={item.quantity} />
      </div>
      <div className="flex-1 pl-2">
        <span className="text-primary-main leading-4 font-medium">
          {formatTitle(item.productTitle)}
        </span>
        <div>
          {!hasNoVariantTitle && (
            <div className="text-grey-700 pt-1 text-sm">
              <FormattedMessage id="express-checkout:cart-items:size" />: {item.variantTitle}
            </div>
          )}

          <IntervalDetails interval={'interval' in item ? item.interval : null} />

          {canRemoveProducts && (
            <button
              data-test="product-remove"
              className="text-text-light-bg-secondary hover:text-text-light-bg-secondary text-sm underline focus:outline-hidden"
              title="Produkt entfernen"
              onClick={onRemoveItem}
            >
              <FormattedMessage id="express-checkout:cart-items:remove" />
            </button>
          )}
        </div>
      </div>
      <div className="text-primary-main ml-5 text-right font-medium">
        {isCartGift ? (
          <span className="uppercase">
            <FormattedMessage id="cart:gift-items:free" />
          </span>
        ) : (
          <>
            {isGiftProductItem(item) ? (
              <span className="text-primary-main">
                <FormattedMessage id="account:orders:point" values={{ points: item.points }} />
              </span>
            ) : (
              <PriceComposition
                className="flex flex-col gap-0"
                color="primary"
                currentPrice={item.price}
                priceBefore={item.compareAtPrice || item.defaultPrice}
                size="md"
                cents
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Item;

const IntervalDetails: FC<{ interval: SubscriptionInterval | null }> = ({ interval }) => (
  <div className="text-grey-700 pt-1 text-sm" data-test="express-checkout-item-interval">
    {interval ? (
      <FormattedMessage id={getIntervalTranslationKeyShort(interval) as string} />
    ) : (
      <FormattedMessage id="cart:interval-selection:one-off" />
    )}
  </div>
);
